/**
 * Contas das ferramentas públicas (/ferramentas). Funções puras: entram
 * números, saem números — a formatação é da tela.
 *
 * Taxas entram em fração (2,5% a.m. = 0.025), nunca em percentual.
 */

/** Parcela fixa pela Tabela Price. Taxa zero vira divisão simples. */
export const pricePayment = (
  principal: number,
  monthlyRate: number,
  installments: number
): number => {
  if (installments <= 0 || principal <= 0) return 0;
  if (monthlyRate <= 0) return principal / installments;
  const factor = (1 + monthlyRate) ** installments;
  return (principal * monthlyRate * factor) / (factor - 1);
};

/** Taxa anual equivalente a uma taxa mensal composta. */
export const annualRate = (monthlyRate: number): number =>
  (1 + monthlyRate) ** 12 - 1;

export type ScheduleRow = {
  number: number;
  payment: number;
  interest: number;
  amortization: number;
  balance: number;
};

/** Tabela mês a mês da Price: juros sobre o saldo, o resto amortiza. */
export const priceSchedule = (
  principal: number,
  monthlyRate: number,
  installments: number
): ScheduleRow[] => {
  const payment = pricePayment(principal, monthlyRate, installments);
  const rows: ScheduleRow[] = [];
  let balance = principal;
  for (let number = 1; number <= installments; number += 1) {
    const interest = balance * monthlyRate;
    // Na última parcela o saldo zera de vez: sem isso o arredondamento deixa
    // "R$ 0,00" negativo na tela.
    const amortization = number === installments ? balance : payment - interest;
    balance = Math.max(balance - amortization, 0);
    rows.push({ number, payment, interest, amortization, balance });
  }
  return rows;
};

/**
 * CET mensal: a taxa que iguala o que o cliente leva financiado (sem a tarifa)
 * ao que ele paga em parcelas (calculadas já com a tarifa embutida). Sem
 * tarifa, o CET é a própria taxa de juros. Resolvido por bisseção — a função é
 * monótona na taxa, então converge sem surpresas.
 */
export const monthlyEffectiveCost = (
  financed: number,
  payment: number,
  installments: number
): number => {
  if (financed <= 0 || payment <= 0 || installments <= 0) return 0;
  const presentValue = (rate: number) =>
    rate === 0
      ? payment * installments
      : (payment * (1 - (1 + rate) ** -installments)) / rate;
  if (presentValue(0) <= financed) return 0;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 100; i += 1) {
    const mid = (low + high) / 2;
    if (presentValue(mid) > financed) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
};

/**
 * Soma meses a uma data mantendo o dia — e, quando o mês não tem esse dia
 * (31 em fevereiro), cai no último dia do mês, como o carnê de papel faria.
 */
export const addMonths = (date: Date, months: number): Date => {
  const day = date.getDate();
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();
  target.setDate(Math.min(day, lastDay));
  return target;
};

export type DelinquencyInput = {
  monthlySales: number;
  /** Fração do valor vendido no crediário que atrasa. */
  delinquencyRate: number;
  /** Fração do valor atrasado que a loja recupera hoje. */
  recoveryRate: number;
  stores: number;
  /** Fração de recuperação usada como referência de comparação. */
  benchmarkRecoveryRate: number;
};

export type DelinquencyResult = {
  overdueMonthly: number;
  recoveredMonthly: number;
  lostMonthly: number;
  lostYearly: number;
  lostYearlyPerStore: number;
  /** Quanto a mais por ano a loja recuperaria na taxa de referência; 0 se já está acima. */
  extraYearlyAtBenchmark: number;
};

export const delinquencyLoss = ({
  monthlySales,
  delinquencyRate,
  recoveryRate,
  stores,
  benchmarkRecoveryRate,
}: DelinquencyInput): DelinquencyResult => {
  const overdueMonthly = monthlySales * delinquencyRate;
  const recoveredMonthly = overdueMonthly * recoveryRate;
  const lostMonthly = overdueMonthly - recoveredMonthly;
  const lostYearly = lostMonthly * 12;
  const extraRate = Math.max(benchmarkRecoveryRate - recoveryRate, 0);
  return {
    overdueMonthly,
    recoveredMonthly,
    lostMonthly,
    lostYearly,
    lostYearlyPerStore: stores > 0 ? lostYearly / stores : lostYearly,
    extraYearlyAtBenchmark: overdueMonthly * extraRate * 12,
  };
};

const currency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export const formatCurrency = (value: number): string =>
  currency.format(Number.isFinite(value) ? value : 0);

export const formatPercent = (fraction: number, digits = 2): string =>
  `${(fraction * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;

export const formatDate = (date: Date): string =>
  date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
